import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import formattingService from '../../../Services/Formatting/FormattingService';
import OverflowCellWithTooltip from '../OverflowCellWithTooltip/OverflowCellWithTooltip';
import styles from './Table.module.scss';
import { TableDatum, TableHeader } from './TableDefinitions';

interface ITableCellProps<T> {
    tableDatum: TableDatum<T>;
    header: TableHeader<T & { actions?: JSX.Element }>;
    datumIndex: number;
    displayDecimalPlaces?: number;
    defaultHeaderOptions?: TableHeader<T & { actions?: JSX.Element }>;
    tableStyle?: 'alternatingRows';
    isTotalsRow?: boolean;
}

const defaultDisplayDecimalPlaces = 2;

// eslint-disable-next-line @typescript-eslint/comma-dangle
const TableCell = <T,>(props: ITableCellProps<T>) => {
    const dataValue = props.tableDatum[props.header.field! as keyof T];
    const displayDecimalPlaces = dataValue
        ? props.header.displayDecimalPlaces ?? props.displayDecimalPlaces ?? formattingService.getDisplayDecimalPlacesMinimum(+dataValue)
        : props.header.displayDecimalPlaces ?? props.displayDecimalPlaces ?? defaultDisplayDecimalPlaces;

    // Classes.
    const numberCellClass =
        props.tableStyle === 'alternatingRows' && props.datumIndex % 2 ? styles.table_data_cell_number_alternating : styles.table_data_cell_number;
    const dataCellClass = props.tableStyle === 'alternatingRows' && props.datumIndex % 2 ? styles.table_data_cell_alternating : styles.table_data_cell;
    const additionalClasses = (
        (props.header.handleGetCellClasses ?? props.defaultHeaderOptions?.handleGetCellClasses)?.(props.tableDatum, props.header) ?? []
    ).join(' ');
    const dataCellClasses = `${dataCellClass} ${additionalClasses}`;
    const numberCellClasses = `${numberCellClass} ${additionalClasses}`;

    // Styles.
    const width = {
        width: props.header.width ?? props.defaultHeaderOptions?.width,
        minWidth: props.header.minimumWidth ?? props.defaultHeaderOptions?.minimumWidth,
    };
    const fontWeight = { fontWeight: props.isTotalsRow ? 700 : 500 };
    const wrapText = { whiteSpace: (props.defaultHeaderOptions?.wrap && props.header.wrap !== false) || props.header.wrap ? 'normal' : 'nowrap' };
    const cellStyles = { ...props.header.cellStyle, ...wrapText, ...width, ...fontWeight } as React.CSSProperties;

    if (props.header.cellRenderer) {
        return (
            <div style={cellStyles} className={dataCellClasses}>
                {props.header.cellRenderer(props.tableDatum, props.header)}
            </div>
        );
    }

    if (props.header.elementField && props.header.dataFormat === 'element') {
        return (
            <div style={cellStyles} className={dataCellClasses}>
                {props.tableDatum[props.header.elementField as keyof T] as unknown as JSX.Element}
            </div>
        );
    }

    if (!dataValue) {
        return (
            <div
                style={cellStyles}
                className={props.header.dataFormat === 'currency' || props.header.dataFormat === 'percent' ? numberCellClass : dataCellClasses}
            >
                {applicationConstants.TablePlaceholderZeroOrEmpty}
            </div>
        );
    }

    if (props.header.dataFormat === 'date') {
        return (
            <div style={cellStyles} className={dataCellClasses}>
                <OverflowCellWithTooltip value={formattingService.toShortDayMonthYear(`${dataValue}`)} />
            </div>
        );
    }

    if (props.header.dataFormat === 'datetime') {
        return (
            <div style={cellStyles} className={dataCellClasses}>
                <OverflowCellWithTooltip value={formattingService.toLongDateAndTimeFormat(`${dataValue}`)} />
            </div>
        );
    }

    if (props.header.dataFormat === 'currency') {
        return (
            <div style={cellStyles} className={numberCellClasses}>
                {formattingService.formatNumber(+dataValue, {
                    minimumDecimalPlaces: displayDecimalPlaces,
                    maximumDecimalPlaces: displayDecimalPlaces,
                })}
            </div>
        );
    }

    if (props.header.dataFormat === 'percent') {
        return (
            <div style={cellStyles} className={numberCellClasses}>
                {formattingService.formatPercentNumber(+dataValue, { precision: displayDecimalPlaces })}
            </div>
        );
    }

    if (props.header.dataFormat === 'number') {
        return (
            <div style={cellStyles} className={numberCellClasses}>
                {formattingService.formatNumber(+dataValue, {
                    minimumDecimalPlaces: displayDecimalPlaces,
                    maximumDecimalPlaces: displayDecimalPlaces,
                })}
            </div>
        );
    }

    return (
        <div style={{ ...props.header.cellStyle, ...cellStyles }} className={dataCellClasses}>
            <OverflowCellWithTooltip value={`${dataValue}`} />
        </div>
    );
};

export default TableCell;
